import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Slider,
  TextField,
  Divider,
  Chip,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Collapse,
} from "@mui/material";
import {
  Calculate as CalculateIcon,
  TipsAndUpdates as TipsIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Savings as SavingsIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { db, auth } from "../../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { TAX_CONFIG_2026 } from "../../utils/taxConfig";

// ============ IRISH TAX CALCULATIONS (2026) ============

// Adapt shared config to local variables for easier use
const TAX_CONFIG = {
  ...TAX_CONFIG_2026,
  dividendWithholdingRate: 0.25,
  closeCompanySurcharge: 0.2,
  employerPrsiRate: TAX_CONFIG_2026.employerPrsiHigherRate, // Default to higher rate for safety
};

// Calculate employee PRSI
function calculateEmployeePRSI(salary) {
  const annualThreshold = TAX_CONFIG.prsiExemptionThreshold * 52;
  if (salary <= annualThreshold) return 0;
  return salary * TAX_CONFIG.employeePrsiRate;
}

// Calculate employer PRSI
function calculateEmployerPRSI(salary) {
  if (salary <= 0) return 0;
  return salary * TAX_CONFIG.employerPrsiRate;
}

// Calculate USC
function calculateUSC(income) {
  if (income <= TAX_CONFIG.uscExemptionThreshold) return 0;

  let usc = 0;
  let remaining = income;

  for (const band of TAX_CONFIG.uscBands) {
    if (remaining <= 0) break;
    const taxableInBand = Math.min(remaining, band.threshold);
    usc += taxableInBand * band.rate;
    remaining -= taxableInBand;
  }

  return usc;
}

// Calculate income tax (PAYE)
function calculateIncomeTax(salary, isDirector = true) {
  if (salary <= 0) return 0;

  let tax = 0;
  const cutoff = TAX_CONFIG.standardRateCutoff;

  if (salary <= cutoff) {
    tax = salary * TAX_CONFIG.standardRate;
  } else {
    tax = cutoff * TAX_CONFIG.standardRate;
    tax += (salary - cutoff) * TAX_CONFIG.higherRate;
  }

  // Apply tax credits
  const credits =
    TAX_CONFIG.personalTaxCredit +
    (isDirector ? TAX_CONFIG.earnedIncomeCredit : TAX_CONFIG.employeeTaxCredit);

  return Math.max(0, tax - credits);
}

// Calculate total personal tax on salary
function calculatePersonalTaxOnSalary(salary, isDirector = true) {
  const incomeTax = calculateIncomeTax(salary, isDirector);
  const prsi = calculateEmployeePRSI(salary);
  const usc = calculateUSC(salary);

  return {
    incomeTax: Math.round(incomeTax * 100) / 100,
    prsi: Math.round(prsi * 100) / 100,
    usc: Math.round(usc * 100) / 100,
    total: Math.round((incomeTax + prsi + usc) * 100) / 100,
    netSalary: Math.round((salary - incomeTax - prsi - usc) * 100) / 100,
    effectiveRate:
      salary > 0
        ? Math.round(((incomeTax + prsi + usc) / salary) * 10000) / 100
        : 0,
  };
}

// Calculate total cost to company of paying salary
function calculateTotalCostToCompany(salary) {
  const employerPrsi = calculateEmployerPRSI(salary);
  return {
    salary,
    employerPrsi: Math.round(employerPrsi * 100) / 100,
    totalCost: Math.round((salary + employerPrsi) * 100) / 100,
  };
}

// Calculate corporation tax saved by paying salary (it's deductible)
function calculateCorpTaxSaved(salary) {
  const totalCost = salary + calculateEmployerPRSI(salary);
  return Math.round(totalCost * TAX_CONFIG.corpTaxRate * 100) / 100;
}

// Find optimal salary for a given available profit
function findOptimalSalary(availableProfit, maritalStatus = "single") {
  // Adjust bands/credits based on marital status
  const cutoff =
    maritalStatus === "married"
      ? TAX_CONFIG.standardRateCutoff + 9000 // Assumption: Transferable band max increase (capped at 53k usually for one earner)
      : TAX_CONFIG.standardRateCutoff;

  const credits =
    maritalStatus === "married"
      ? TAX_CONFIG.personalTaxCredit * 2 + TAX_CONFIG.earnedIncomeCredit // Married credit is double personal
      : TAX_CONFIG.personalTaxCredit + TAX_CONFIG.earnedIncomeCredit;

  // Helper to calculate with specific bands
  const calcTax = (salary) => {
    let tax = 0;
    if (salary <= cutoff) {
      tax = salary * TAX_CONFIG.standardRate;
    } else {
      tax = cutoff * TAX_CONFIG.standardRate;
      tax += (salary - cutoff) * TAX_CONFIG.higherRate;
    }
    return Math.max(0, tax - credits);
  };

  const scenarios = [];
  const testPoints = [
    0,
    18304,
    25000,
    35000,
    42000,
    44000,
    50000,
    53000,
    60000,
    75000,
    100000,
  ];

  for (const salary of testPoints) {
    if (salary > availableProfit * 1.2) continue; // Skip unrealistic amounts

    // Custom personal tax calc for this loop to respect dynamic cutoff/credits
    const incomeTax = calcTax(salary);
    const prsi = calculateEmployeePRSI(salary);
    const usc = calculateUSC(salary);
    const totalPersonalTax = incomeTax + prsi + usc;
    const netSalary = salary - totalPersonalTax;
    const effectiveRate =
      salary > 0
        ? Math.round((totalPersonalTax / salary) * 10000) / 100
        : 0;

    const companyCost = calculateTotalCostToCompany(salary);
    const corpTaxSaved = calculateCorpTaxSaved(salary);

    scenarios.push({
      salary,
      netToDirector: Math.round(netSalary * 100) / 100,
      totalCostToCompany: companyCost.totalCost,
      corpTaxSaved,
      effectiveRate,
      employerPrsi: companyCost.employerPrsi,
    });
  }

  return scenarios;
}

export default function DirectorSalaryOptimizer() {
  const [availableProfit, setAvailableProfit] = useState(50000);
  const [customSalary, setCustomSalary] = useState(35000);
  const [maritalStatus, setMaritalStatus] = useState("single");
  const [showDetails, setShowDetails] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [businessData, setBusinessData] = useState({ income: 0, expenses: 0 });

  // Fetch actual business data
  useEffect(() => {
    const fetchBusinessData = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoadingData(false);
        return;
      }

      const currentYear = new Date().getFullYear();

      try {
        // Fetch invoices
        const invoicesQ = query(
          collection(db, "invoices"),
          where("userId", "==", user.uid),
          where("invoiceYear", "==", currentYear)
        );
        const invoicesSnap = await getDocs(invoicesQ);
        const income = invoicesSnap.docs.reduce((sum, doc) => {
          const amt = parseFloat(
            doc.data().subtotal || doc.data().totalAmount || 0
          );
          return sum + (isNaN(amt) ? 0 : amt);
        }, 0);

        // Fetch expenses
        const receiptsQ = query(
          collection(db, "receipts"),
          where("uid", "==", user.uid),
          where("receiptYear", "==", currentYear)
        );
        const receiptsSnap = await getDocs(receiptsQ);
        const expenses = receiptsSnap.docs.reduce((sum, doc) => {
          const amt = parseFloat(doc.data().subtotal || doc.data().amount || 0);
          return sum + (isNaN(amt) ? 0 : amt);
        }, 0);

        const profit = Math.max(0, income - expenses);
        setBusinessData({ income, expenses });
        setAvailableProfit(Math.round(profit));
        setCustomSalary(Math.min(42000, Math.round(profit * 0.7)));
      } catch (err) {
        console.error("Failed to fetch business data:", err);
      } finally {
        setLoadingData(false);
      }
    };

    fetchBusinessData();
  }, []);



  // Recalculate personal tax based on Marital Status for the "Key Stats" cards
  // Note: calculatePersonalTaxOnSalary uses default TAX_CONFIG, so we need to adjust standard rate cutoff manually here for display if we want accuracy or update helper
  // For simplicity, we'll re-implement the specific cut-off logic for the display variables
  const getPersonalTaxDisplay = (salary) => {
    const cutoff =
      maritalStatus === "married"
        ? TAX_CONFIG.standardRateCutoff + 9000
        : TAX_CONFIG.standardRateCutoff;
    const credits =
      maritalStatus === "married"
        ? TAX_CONFIG.personalTaxCredit * 2 + TAX_CONFIG.earnedIncomeCredit
        : TAX_CONFIG.personalTaxCredit + TAX_CONFIG.earnedIncomeCredit;

    let incomeTax = 0;
    if (salary <= cutoff) {
      incomeTax = salary * TAX_CONFIG.standardRate;
    } else {
      incomeTax = cutoff * TAX_CONFIG.standardRate;
      incomeTax += (salary - cutoff) * TAX_CONFIG.higherRate;
    }
    incomeTax = Math.max(0, incomeTax - credits);

    const prsi = calculateEmployeePRSI(salary);
    const usc = calculateUSC(salary);
    const total = incomeTax + prsi + usc;

    return {
      incomeTax: Math.round(incomeTax * 100) / 100,
      prsi: Math.round(prsi * 100) / 100,
      usc: Math.round(usc * 100) / 100,
      total: Math.round(total * 100) / 100,
      netSalary: Math.round((salary - total) * 100) / 100,
      effectiveRate:
        salary > 0 ? Math.round((total / salary) * 10000) / 100 : 0,
    };
  };

  const personalTax = getPersonalTaxDisplay(customSalary);
  const companyCost = calculateTotalCostToCompany(customSalary);
  const corpTaxSaved = calculateCorpTaxSaved(customSalary);
  const scenarios = findOptimalSalary(availableProfit, maritalStatus);

  // Calculate what's left after paying salary
  const remainingProfit = availableProfit - companyCost.totalCost;
  const corpTaxOnRemaining =
    remainingProfit > 0 ? remainingProfit * TAX_CONFIG.corpTaxRate : 0;
  const retainedEarnings = remainingProfit - corpTaxOnRemaining;

  // Find optimal (best net to director with reasonable cost)
  const optimalScenario = scenarios.reduce((best, current) => {
    if (current.totalCostToCompany > availableProfit) return best;
    if (!best) return current;
    // Prefer higher net with reasonable effective rate
    if (
      current.netToDirector > best.netToDirector &&
      current.effectiveRate < 50
    ) {
      return current;
    }
    return best;
  }, null);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: "linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <CalculateIcon sx={{ color: "primary.main", fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700}>
          Director Salary Optimizer
        </Typography>
        <Chip
          label="Ireland 2026"
          size="small"
          color="primary"
          variant="outlined"
        />
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          As a director of your own Irish limited company, you can optimise how
          much salary to pay yourself to minimise overall tax. This calculator
          helps you find the sweet spot between personal tax, employer's PRSI,
          and corporation tax savings.
        </Typography>
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Director Marital Status
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={maritalStatus}
          exclusive
          onChange={(e, val) => val && setMaritalStatus(val)}
          size="small"
        >
          <ToggleButton value="single">Single / Individual</ToggleButton>
          <ToggleButton value="married">Married (Single Income)</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          *Married (Single Income) benefits from a higher rate band and transferred credits.
        </Typography>
      </Box>

      {/* Profit Input */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Available Company Profit (before your salary)
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="number"
            size="small"
            value={availableProfit}
            onChange={(e) =>
              setAvailableProfit(Math.max(0, Number(e.target.value)))
            }
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>€</Typography>,
            }}
            sx={{ width: 180 }}
          />
          {!loadingData && businessData.income > 0 && (
            <Chip
              label={`Based on €${businessData.income.toLocaleString()} income - €${businessData.expenses.toLocaleString()} expenses`}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Salary Slider */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Your Annual Salary: €{customSalary.toLocaleString()}
        </Typography>
        <Slider
          value={customSalary}
          onChange={(_, v) => setCustomSalary(v)}
          min={0}
          max={Math.min(150000, availableProfit * 1.5)}
          step={1000}
          marks={[
            { value: 18304, label: "No PRSI" },
            { value: 42000, label: "20% band" },
            { value: 75000, label: "" },
          ]}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `€${v.toLocaleString()}`}
        />
      </Box>

      {/* Results Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{ height: "100%", bgcolor: "background.paper" }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PersonIcon color="primary" />
                <Typography variant="subtitle2" fontWeight={600}>
                  You Receive
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700} color="success.main">
                €{personalTax.netSalary.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Net salary after tax ({personalTax.effectiveRate}% effective
                rate)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{ height: "100%", bgcolor: "background.paper" }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <AccountBalanceIcon color="warning" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Company Cost
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                €{companyCost.totalCost.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Salary + €{companyCost.employerPrsi.toLocaleString()} employer
                PRSI
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            variant="outlined"
            sx={{ height: "100%", bgcolor: "background.paper" }}
          >
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <SavingsIcon color="info" />
                <Typography variant="subtitle2" fontWeight={600}>
                  Retained Earnings
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700} color="info.main">
                €{Math.max(0, Math.round(retainedEarnings)).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                After €{Math.round(corpTaxOnRemaining).toLocaleString()}{" "}
                corporation tax
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tax Breakdown - Expandable */}
      <Button
        onClick={() => setShowDetails(!showDetails)}
        endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ mb: 2 }}
      >
        {showDetails ? "Hide" : "Show"} Tax Breakdown
      </Button>

      <Collapse in={showDetails}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Personal Tax on €{customSalary.toLocaleString()} Salary
              </Typography>
              <TaxRow label="Income Tax (PAYE)" value={personalTax.incomeTax} />
              <TaxRow label="Employee PRSI (4%)" value={personalTax.prsi} />
              <TaxRow label="USC" value={personalTax.usc} />
              <Divider sx={{ my: 1 }} />
              <TaxRow
                label="Total Personal Tax"
                value={personalTax.total}
                bold
                color="error.main"
              />
              <TaxRow
                label="Net Salary"
                value={personalTax.netSalary}
                bold
                color="success.main"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Company Costs & Savings
              </Typography>
              <TaxRow label="Gross Salary" value={customSalary} />
              <TaxRow
                label="Employer PRSI (11.05%)"
                value={companyCost.employerPrsi}
              />
              <Divider sx={{ my: 1 }} />
              <TaxRow
                label="Total Cost to Company"
                value={companyCost.totalCost}
                bold
              />
              <TaxRow
                label="Corp Tax Saved (12.5%)"
                value={corpTaxSaved}
                color="success.main"
              />
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Recommendation */}
      {optimalScenario && (
        <Alert severity="success" icon={<TipsIcon />} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            💡 Recommendation
          </Typography>
          <Typography variant="body2">
            Based on your €{availableProfit.toLocaleString()} profit, consider a
            salary of{" "}
            <strong>€{optimalScenario.salary.toLocaleString()}</strong> for
            optimal tax efficiency. You'd receive{" "}
            <strong>€{optimalScenario.netToDirector.toLocaleString()}</strong>{" "}
            net at an effective tax rate of {optimalScenario.effectiveRate}%.
            {customSalary <= 18304 &&
              " At this level, you pay no employee PRSI!"}
            {customSalary > 18304 &&
              customSalary <= 42000 &&
              " This keeps you in the 20% tax band."}
          </Typography>
        </Alert>
      )}

      {/* Key Thresholds Info */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          <strong>Key Irish Tax Thresholds (2026):</strong> €
          {TAX_CONFIG.prsiExemptionThreshold * 52} (PRSI exemption) • €
          {TAX_CONFIG.standardRateCutoff} (20% tax band limit for single person)
          • {(TAX_CONFIG.employerPrsiRate * 100).toFixed(2)}% employer PRSI •{" "}
          {(TAX_CONFIG.corpTaxRate * 100).toFixed(1)}% corporation tax on
          profits
        </Typography>
      </Box>
    </Paper>
  );
}

function TaxRow({ label, value, bold, color }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" fontWeight={bold ? 600 : 400}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 700 : 500}
        color={color || "text.primary"}
      >
        €{value.toLocaleString()}
      </Typography>
    </Stack>
  );
}
